import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
  Engineering as EngineeringIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { SignupData } from '../../pages/Auth/SignupPage';
import { ToolkitSelectionDialog, Toolkit } from './ToolkitSelectionDialog';

interface SignupSubscriptionStepProps {
  data: SignupData;
  onUpdate: (data: Partial<SignupData>) => void;
  onNext: () => void;
  onBack: () => void;
}

interface PlanFeature {
  text: string;
  highlight?: boolean;
}

interface SubscriptionPlan {
  id: 'starter' | 'professional' | 'enterprise' | 'industry_specific';
  name: string;
  icon: React.ReactNode;
  price: {
    monthly: number;
    annual: number;
  };
  description: string;
  features: PlanFeature[];
  recommended?: boolean;
}

const plans: SubscriptionPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    icon: <StarIcon style={{ fontSize: 20 }} />,
    price: {
      monthly: 29,
      annual: 290,
    },
    description: 'Individuals & small teams',
    features: [
      { text: '5 custom AI teammates', highlight: true },
      { text: 'Basic knowledge graph' },
      { text: 'Up to 5 team members' },
      { text: '10GB storage' },
      { text: 'Email support' },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    icon: <RocketIcon style={{ fontSize: 20 }} />,
    price: {
      monthly: 59,
      annual: 590,
    },
    description: 'Growing teams',
    features: [
      { text: '10 custom AI teammates', highlight: true },
      { text: 'Advanced knowledge graph', highlight: true },
      { text: 'Unlimited team members' },
      { text: '100GB storage' },
      { text: 'Priority support' },
      { text: 'API access' },
    ],
    recommended: true,
  },
  // Temporarily hidden - uncomment to show Enterprise option
  // {
  //   id: 'enterprise',
  //   name: 'Enterprise',
  //   icon: <BusinessIcon style={{ fontSize: 20 }} />,
  //   price: {
  //     monthly: 89,
  //     annual: 890,
  //   },
  //   description: 'Large organizations',
  //   features: [
  //     { text: 'All Pro features' },
  //     { text: 'Unlimited custom agents', highlight: true },
  //     { text: 'Enterprise SSO', highlight: true },
  //     { text: 'Unlimited storage' },
  //     { text: 'Dedicated support' },
  //     { text: 'Custom integrations' },
  //     { text: 'SLA guarantee' },
  //     { text: 'On-premise option' },
  //   ],
  // },
  {
    id: 'industry_specific',
    name: 'Industry Specific',
    icon: <EngineeringIcon style={{ fontSize: 20 }} />,
    price: {
      monthly: 129,
      annual: 1290,
    },
    description: 'Specialized industry toolkits',
    features: [
      { text: 'All Pro features' },
      { text: 'Industry-specific AI teammates', highlight: true },
      { text: 'Professional tools & calculators', highlight: true },
      { text: 'Compliance & standards support', highlight: true },
      { text: 'Specialized document processing', highlight: true },
      { text: 'Industry data integration' },
      { text: 'Expert validation tools' },
      { text: 'Priority engineering support' },
    ],
  },
];

export const SignupSubscriptionStep: React.FC<SignupSubscriptionStepProps> = ({
  data,
  onUpdate,
  onNext,
  onBack,
}) => {
  const [toolkitDialogOpen, setToolkitDialogOpen] = useState(false);
  const [selectedToolkit, setSelectedToolkit] = useState<Toolkit | null>(null);

  const handlePlanSelect = (planId: 'starter' | 'professional' | 'enterprise' | 'industry_specific') => {
    if (planId === 'industry_specific') {
      // Open toolkit selection dialog instead of directly selecting the plan
      setToolkitDialogOpen(true);
    } else {
      // Clear any selected toolkit if switching away from industry_specific
      setSelectedToolkit(null);
      onUpdate({
        subscriptionTier: planId,
        selectedToolkit: undefined
      });
    }
  };

  const handleToolkitSelect = (toolkit: Toolkit) => {
    setSelectedToolkit(toolkit);
    onUpdate({
      subscriptionTier: 'industry_specific',
      selectedToolkit: toolkit.code
    });
  };

  const handleBillingChange = (
    event: React.MouseEvent<HTMLElement>,
    newBilling: 'monthly' | 'annual'
  ) => {
    if (newBilling !== null) {
      onUpdate({ billingFrequency: newBilling });
    }
  };

  const calculatePrice = (plan: SubscriptionPlan) => {
    const basePrice = plan.price[data.billingFrequency];
    if (data.billingFrequency === 'annual') {
      return {
        total: basePrice,
        monthly: Math.round(basePrice / 12),
        savings: plan.price.monthly * 12 - basePrice,
      };
    }
    return {
      total: basePrice,
      monthly: basePrice,
      savings: 0,
    };
  };

  return (
    <Paper elevation={3} style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography variant="h6" fontWeight="bold" style={{ fontSize: '1.1rem' }}>
          Choose Your Plan
        </Typography>
        <ToggleButtonGroup
          value={data.billingFrequency}
          exclusive
          onChange={handleBillingChange}
          aria-label="billing frequency"
          size="small"
          style={{ height: 34 }}
        >
          <ToggleButton value="monthly" aria-label="monthly billing" style={{ paddingTop: 4, paddingBottom: 4, paddingLeft: 16, paddingRight: 16, fontSize: '0.9rem' }}>
            Monthly
          </ToggleButton>
          <ToggleButton value="annual" aria-label="annual billing" style={{ paddingTop: 4, paddingBottom: 4, paddingLeft: 16, paddingRight: 16, fontSize: '0.9rem' }}>
            Annual (Save 17%)
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <Grid container spacing={1} style={{ flex: 1, alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
        {plans.map((plan) => {
          const pricing = calculatePrice(plan);
          const isSelected = data.subscriptionTier === plan.id;

          return (
            <Grid item xs={12} sm={4} key={plan.id}>
              <Card
                style={{
                  height: 'auto',
                  minHeight: 380,
                  maxHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: isSelected ? '2px solid #325BA8' : '1px solid rgba(0,0,0,0.12)',
                }}
              >
                {plan.recommended && (
                  <Chip
                    label="BEST"
                    color="secondary"
                    size="small"
                    style={{
                      position: 'absolute',
                      top: -10,
                      right: 10,
                      fontSize: '0.6rem',
                      height: 18,
                      zIndex: 1,
                    }}
                  />
                )}

                <CardContent style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ textAlign: 'center', marginBottom: 8 }}>
                    <div style={{ color: '#325BA8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                      {plan.icon}
                      <Typography variant="body1" component="h3" fontWeight="bold" style={{ fontSize: '1rem' }}>
                        {plan.name}
                      </Typography>
                    </div>
                    <Typography variant="body2" color="text.secondary" style={{ display: 'block', fontSize: '0.85rem', lineHeight: 1.3 }}>
                      {plan.description}
                    </Typography>
                    <div style={{ marginTop: 8 }}>
                      <Typography variant="h5" component="span" fontWeight="bold">
                        ${pricing.monthly}
                      </Typography>
                      <Typography variant="body2" component="span" color="text.secondary">
                        /mo
                      </Typography>
                      {data.billingFrequency === 'annual' && pricing.savings > 0 && (
                        <Typography variant="body2" style={{ color: '#2e7d32', display: 'block', fontSize: '0.8rem' }}>
                          Save ${pricing.savings}/yr
                        </Typography>
                      )}
                    </div>
                  </div>

                  <List dense style={{ paddingTop: 0, paddingBottom: 0, flex: 1 }}>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 2, paddingBottom: 2 }}>
                        <ListItemIcon style={{ minWidth: 24 }}>
                          <CheckIcon
                            color={feature.highlight ? 'secondary' : 'action'}
                            style={{ fontSize: 16 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            lineHeight: 1.3,
                            fontWeight: feature.highlight ? 'medium' : 'normal',
                            color: feature.highlight ? 'secondary.main' : 'text.primary',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <CardActions style={{ padding: 8 }}>
                  {plan.id === 'industry_specific' ? (
                    <div style={{ width: '100%' }}>
                      {selectedToolkit ? (
                        <div>
                          <Chip
                            label={selectedToolkit.name}
                            color="secondary"
                            size="small"
                            onDelete={() => setToolkitDialogOpen(true)}
                            deleteIcon={<AddIcon />}
                            style={{ marginBottom: 8, width: '100%' }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => setToolkitDialogOpen(true)}
                            style={{ fontSize: '0.85rem', paddingTop: 6, paddingBottom: 6 }}
                          >
                            Change Toolkit
                          </Button>
                        </div>
                      ) : (
                        <Button
                          fullWidth
                          variant={isSelected ? 'contained' : 'outlined'}
                          color="primary"
                          size="small"
                          onClick={() => handlePlanSelect(plan.id)}
                          startIcon={<AddIcon />}
                          style={{ fontSize: '0.85rem', paddingTop: 6, paddingBottom: 6 }}
                        >
                          Select Toolkit
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      fullWidth
                      variant={isSelected ? 'contained' : 'outlined'}
                      color="primary"
                      size="small"
                      onClick={() => handlePlanSelect(plan.id)}
                      style={{ fontSize: '0.85rem', paddingTop: 6, paddingBottom: 6 }}
                    >
                      {isSelected ? 'Selected' : 'Select Plan'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack} size="medium">
          Back
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={onNext}
          disabled={!data.subscriptionTier}
          style={{ paddingLeft: 24, paddingRight: 24 }}
        >
          Next: Setup Organization
        </Button>
      </div>

      <ToolkitSelectionDialog
        open={toolkitDialogOpen}
        onClose={() => setToolkitDialogOpen(false)}
        onSelect={handleToolkitSelect}
        billingFrequency={data.billingFrequency}
        selectedToolkitId={selectedToolkit?.id}
      />
    </Paper>
  );
};
